import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { MascotDoodle } from "@/components/brand/MascotDoodle";
import { cartPayload, useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { createCheckout, errText, getMyCodes, quoteCart } from "@/lib/api";
import { allTokens } from "@/lib/access";
import { cad, FREE_SHIP, POSTAL_RE, PROVINCES } from "@/lib/site";

const EMPTY = { email: "", name: "", phone: "", line1: "", line2: "", city: "", province: "ON", postal_code: "" };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/;

const validate = (f, physical) => {
  const e = {};
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Enter a valid email";
  if (f.name.trim().length < 2) e.name = "Enter your full name";
  if (!physical) return e;
  if (f.line1.trim().length < 3) e.line1 = "Enter your street address";
  if (f.city.trim().length < 2) e.city = "Enter your city";
  if (!POSTAL_RE.test(f.postal_code.trim())) e.postal_code = "Format: A1A 1A1";
  return e;
};

const Field = ({ id, label, form, set, errors, optional, ...rest }) => (
  <label className="block" htmlFor={`co-${id}`}>
    <span className="label text-ink/60">{label}{optional && <span className="text-ink/40"> · optional</span>}</span>
    <input id={`co-${id}`} value={form[id]} onChange={(e) => set(id, e.target.value)} aria-invalid={!!errors[id]} className="field" data-testid={`checkout-${id}`} {...rest} />
    {errors[id] && <span className="mt-1 block text-[12px] text-alert" data-testid={`checkout-${id}-error`}>{errors[id]}</span>}
  </label>
);

const Summary = ({ items, q }) => (
  <aside className="rounded-[24px] bg-sky/25 p-6 sm:p-8 lg:sticky lg:top-40" data-testid="checkout-summary">
    <p className="label font-bold">Order summary</p>
    <ul className="mt-5">
      {items.map((i) => (
        <li key={i.key} className="flex gap-4 border-b border-ink/10 py-4" data-testid={`checkout-line-${i.key}`}>
          <div className="relative h-16 w-16 shrink-0 rounded-[12px] bg-[#ECEBE4]"><img src={i.image} alt={i.name} className="h-full w-full rounded-[12px] object-cover mix-blend-multiply" /><span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-ink px-1 font-mono text-[10px] text-cloud">{i.qty}</span></div>
          <div className="flex flex-1 justify-between gap-2 text-sm"><div><p>{i.name}</p><p className="label mt-1 !text-[10px] text-ink/60">{i.description || `${i.color} · ${i.size === "OS" ? "One size" : i.size}`}</p></div><span>{cad(i.price * i.qty)}</span></div>
        </li>
      ))}
    </ul>
    {q && <dl className="space-y-2 pt-5 text-sm">
      <div className="flex justify-between"><dt className="text-ink/65">Subtotal</dt><dd data-testid="checkout-subtotal">{cad(q.subtotal)}</dd></div>
      {q.discount > 0 && <div className="flex justify-between"><dt className="text-ink/65">Crew reward {q.discount_code}</dt><dd data-testid="checkout-discount">−{cad(q.discount)}</dd></div>}
      <div className="flex justify-between"><dt className="text-ink/65">Shipping</dt><dd data-testid="checkout-shipping">{!q.physical ? "None (gift cards)" : q.shipping_fee === 0 ? "FREE" : cad(q.shipping_fee)}</dd></div>
      {q.gift_card_applied > 0 && <div className="flex justify-between"><dt className="text-ink/65">Gift card</dt><dd data-testid="checkout-gift-applied">−{cad(q.gift_card_applied)}</dd></div>}
      <div className="flex justify-between border-t border-ink/15 pt-3 text-base font-medium"><dt>Total (CAD)</dt><dd data-testid="checkout-total">{cad(q.total)}</dd></div>
    </dl>}
    {q?.physical && <p className="label mt-5 !text-[10px] text-ink/60">{q.merch_subtotal >= FREE_SHIP ? "Free shipping unlocked" : `Free shipping over ${cad(FREE_SHIP)}`}</p>}
  </aside>
);

const CodeField = ({ id, label, value, applied, onApply, onClear, chips = [] }) => {
  const [v, setV] = useState(value || "");
  return (
    <div data-testid={`checkout-${id}-box`}>
      <span className="label text-ink/60">{label}</span>
      {applied ? <p className="mt-3 flex items-center justify-between rounded-[12px] bg-lime px-4 py-3 text-sm"><span className="font-mono font-bold" data-testid={`checkout-${id}-applied`}>{applied}</span><button type="button" onClick={() => { setV(""); onClear(); }} className="link-u" data-testid={`checkout-${id}-remove`}>Remove</button></p> : (
        <div className="mt-2 flex gap-3"><input value={v} onChange={(e) => setV(e.target.value.toUpperCase())} className="field !mt-0 flex-1 font-mono" data-testid={`checkout-${id}-input`} /><button type="button" onClick={() => v.trim() && onApply(v.trim())} className="btn btn-ink !h-12" data-testid={`checkout-${id}-apply`}>Apply</button></div>)}
      {!applied && chips.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{chips.map((c) => <button type="button" key={c.code} onClick={() => onApply(c.code)} className="label rounded-full border border-ink px-3 py-1" data-testid={`checkout-${id}-chip-${c.code}`}>{c.code} · {c.name}</button>)}</div>}
    </div>
  );
};

export default function Checkout() {
  const { items, setOpen } = useCart();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState({ discount_code: null, gift_card_code: null });
  const [q, setQ] = useState(null);
  const [myCodes, setMyCodes] = useState([]);
  const [saveAddr, setSaveAddr] = useState(false);
  const inFlight = useRef(false);
  const attemptKey = useRef(window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const cancelled = params.get("cancelled") === "1";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const payload = cartPayload(items);
  const physical = payload.items.length + payload.bundles.length > 0;
  const sig = JSON.stringify(payload) + JSON.stringify(codes) + (user?.id || "");

  useEffect(() => { if (cancelled) toast("Payment cancelled. Your cart is right where you left it."); }, [cancelled]);
  useEffect(() => {
    if (!user) return;
    const a = user.addresses[0];
    setForm((f) => ({ ...f, email: f.email || user.email, name: f.name || user.name, ...(a && !f.line1 ? { line1: a.line1, line2: a.line2 || "", city: a.city, province: a.province, postal_code: a.postal_code } : {}) }));
    getMyCodes().then((c) => setMyCodes(c.filter((x) => !x.used_at && x.type !== "stickers"))).catch(() => {});
  }, [user]);
  useEffect(() => {
    if (!items.length) return;
    quoteCart({ ...payload, ...codes, access_tokens: Object.values(allTokens()) }).then(setQ).catch((e) => {
      toast.error(errText(e));
      setCodes((c) => (c.discount_code || c.gift_card_code ? { discount_code: null, gift_card_code: null } : c));
    });
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    const errs = validate(form, physical);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const { email, name, phone, ...ship } = form;
      const r = await createCheckout({
        ...payload, ...codes, customer: { email, name, phone: phone || null }, shipping: physical ? ship : null, save_address: saveAddr, origin_url: window.location.origin,
        idempotency_key: attemptKey.current, access_tokens: Object.values(allTokens()),
      });
      window.location.href = r.checkout_url;
    } catch (err) {
      toast.error(errText(err));
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (!items.length) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 sm:px-10 lg:py-32" data-testid="checkout-empty">
        <Seo title="Checkout" description="Checkout at the suspects." path="/checkout" noindex />
        <MascotDoodle variant="headphones" size={130} bg="#7FD3FF" />
        <h1 className="display mt-10 text-[clamp(3.5rem,8vw,6rem)]">Your cart is empty</h1>
        <Link to="/shop" className="btn btn-ink mt-10" data-testid="checkout-back-to-shop">Shop the drop <ArrowRight size={16} /></Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-12 sm:px-10 lg:pt-20" data-testid="checkout-page">
      <Seo title="Checkout" description="Checkout at the suspects." path="/checkout" noindex />
      {cancelled && <div className="mb-10 flex flex-wrap items-center justify-between gap-4 bg-pink px-5 py-4" data-testid="checkout-cancelled-banner"><p className="label font-bold">Payment cancelled. Nothing was charged and your cart is saved.</p><button onClick={() => setOpen(true)} className="link-u" data-testid="checkout-open-cart">View cart</button></div>}
      <h1 className="display text-[clamp(3.5rem,9vw,8rem)]"><span className="hl hl-sky hl-thick">Checkout</span></h1>
      <div className="mt-14 grid gap-14 lg:grid-cols-12 lg:gap-20">
        <form onSubmit={submit} noValidate className="space-y-12 lg:col-span-7" data-testid="checkout-form">
          <fieldset className="space-y-6"><legend className="label mb-6 font-bold">01 · Contact</legend>
            <Field id="email" label="Email" type="email" autoComplete="email" form={form} set={set} errors={errors} />
            <div className="grid gap-6 sm:grid-cols-2">
              <Field id="name" label="Full name" autoComplete="name" form={form} set={set} errors={errors} />
              <Field id="phone" label="Phone" type="tel" autoComplete="tel" optional form={form} set={set} errors={errors} />
            </div>
          </fieldset>
          {physical && <fieldset className="space-y-6"><legend className="label mb-6 font-bold">02 · Shipping address (Canada)</legend>
            {user?.addresses.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="checkout-saved-addresses">{user.addresses.map((a, i) => (
                <button type="button" key={a.line1 + i} onClick={() => setForm((f) => ({ ...f, line1: a.line1, line2: a.line2 || "", city: a.city, province: a.province, postal_code: a.postal_code }))} className={`label rounded-full border-[1.5px] border-ink px-4 py-2 ${form.line1 === a.line1 ? "bg-ink text-cloud" : ""}`} data-testid={`checkout-saved-address-${i}`}>{a.label}: {a.line1}</button>))}</div>
            )}
            <Field id="line1" label="Street address" autoComplete="address-line1" form={form} set={set} errors={errors} />
            <Field id="line2" label="Apt, suite, unit" autoComplete="address-line2" optional form={form} set={set} errors={errors} />
            <div className="grid gap-6 sm:grid-cols-3">
              <Field id="city" label="City" autoComplete="address-level2" form={form} set={set} errors={errors} />
              <label className="block" htmlFor="co-province"><span className="label text-ink/60">Province</span>
                <select id="co-province" value={form.province} onChange={(e) => set("province", e.target.value)} className="field" data-testid="checkout-province">
                  {PROVINCES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select></label>
              <Field id="postal_code" label="Postal code" autoComplete="postal-code" placeholder="M5V 2T6" form={form} set={set} errors={errors} />
            </div>
            {user && <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={saveAddr} onChange={(e) => setSaveAddr(e.target.checked)} data-testid="checkout-save-address" /> Save this address to my account</label>}
          </fieldset>}
          <fieldset className="grid gap-6 sm:grid-cols-2"><legend className="label mb-6 font-bold">{physical ? "03" : "02"} · Codes</legend>
            {user ? <CodeField id="discount" label="Crew reward code" applied={q?.discount_code} chips={myCodes} onApply={(c) => setCodes((x) => ({ ...x, discount_code: c }))} onClear={() => setCodes((x) => ({ ...x, discount_code: null }))} />
              : <p className="text-sm text-ink/60"><Link to="/account" className="link-u">Log in</Link> to use Crew reward codes and earn points on this order.</p>}
            <CodeField id="giftcard" label="Gift card" applied={q?.gift_card_code} onApply={(c) => setCodes((x) => ({ ...x, gift_card_code: c }))} onClear={() => setCodes((x) => ({ ...x, gift_card_code: null }))} />
          </fieldset>
          <div>
            <button type="submit" disabled={busy || !q} className="btn btn-lime h-16 w-full sm:w-auto sm:px-12" data-testid="checkout-pay-button"><Lock size={15} /> {busy ? "Placing order…" : q?.total === 0 ? "Place order · covered by gift card" : `Pay ${cad(q?.total ?? 0)}`} <ArrowRight size={16} /></button>
            <p className="label mt-5 !text-[10px] text-ink/60">{user ? `Logged in as ${user.email}. You'll earn ${Math.floor(q?.points_base || 0)} Crew Points when it's paid.` : "You'll finish payment on Stripe's secure page. Guest checkout, no account needed."}</p>
          </div>
        </form>
        <div className="lg:col-span-5"><Summary items={items} q={q} /></div>
      </div>
    </div>
  );
}
